import React from 'react';
import ReactDOMServer from 'react-dom/server';
import Clipboard from 'clipboard';
import moment from 'moment';
import dasherize from 'dasherize'
import qp from 'query-parse';
import jwtDecode from 'jwt-decode';
import DOMPurify from 'dompurify';

import store from '../store';
import config from '../config';

import { createAlert } from '../services/alert-service';
import { shortenUrl } from '../api/global-api';

export const convertBytesToHumanReadable = (bytes) => {
    const i = Math.max(0, Math.floor(Math.log(bytes) / Math.log(1024)));
    return (bytes / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
}

export const copyToClipboard = text => {
    const element = document.createElement('span');
    const clipboard = new Clipboard(element);
    element.setAttribute('data-clipboard-text', text);
    document.body.appendChild(element);
    element.click();
    clipboard.destroy();
    document.body.removeChild(element);

    createAlert('success', <span><i className="icon material-icons">check_circle</i> Copied to clipboard.</span>);
}

export const copyUrlToClipboard = (url, publicUrl) => {
    shortenUrl(url, link => {
        copyToClipboard(link + (publicUrl ? '?pub' : ''));
    }, () => {
        copyToClipboard(url);
    });
}

export const formatCalendarDate = (date, locale) => {
    return moment(date).calendar(null, {
        sameDay: '[Today]',
        nextDay: '[Tomorrow]',
        nextWeek: locale.dateTimeFormats.longDate,
        lastDay: '[Yesterday]',
        lastWeek: locale.dateTimeFormats.longDate,
        sameElse: locale.dateTimeFormats.longDate
    });
}

export const throttle = (func, wait, context = this) => {
    let timeout;
    let funcArgs;

    const later = () => {
        func.apply(context, funcArgs);
        timeout = null;
    }

    return (...args) => {
        funcArgs = args;
        if (!timeout) {
            timeout = setTimeout(later, wait);
        }
    }
}


export const debounce = (func, wait, context = this) => {
  let timeout;

    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
    }
}

export const urlify = input => input.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '-').replace(/^-+|-+$/g, '');

export const getCleanIdFromUrl = (input, divider = '-') => {
    if (!input || input.match(/^.{1,4}-\d+$/)) { // if matched nice id
        return input;
    }

    return input.split(divider)[0];
}

export const createSlug = (input = '') => {
    return urlify(dasherize(input.replace(/\s/g, '-')))
}

export const delegateHover = (holder, selectorClassName, cb) => {
    const handler = event => {
        if (!event.target.classList) {
            return null;
        }

        if (event.target.classList.contains(selectorClassName) || (event.target.closest && event.target.closest(`.${selectorClassName}`))) {
            cb(event);
        }
    }

    const unbind = () => {
        holder.removeEventListener('mouseover', handler, false);
    }

    holder.addEventListener('mouseover', handler, false);

    return unbind;
}

export const getElementRect = element => {
    const appTop = document.querySelector('.app-inner').getBoundingClientRect().top;
    const elementRect = element.getBoundingClientRect();
    const top = elementRect.top - appTop + window.scrollY;
    const left = elementRect.left + window.scrollX;

    return {
        width: elementRect.width,
        height: elementRect.height,
        top,
        left
    }
}

export const hardReload = () => {
    window.location.reload(true);
}

export const isSafari = /constructor/i.test(window.HTMLElement) || (function (p) { return p.toString() === "[object SafariRemoteNotification]"; })(!window['safari'] || (typeof window.safari !== 'undefined' && window.safari.pushNotification));

export const createAppUrl = () => {
    return `https://${store.getState().auth.teamUrl}.${config.APP_DOMAIN}`;
}

export const highlightText = (text, higlight, returnAsString) => {
    const parts = text.split(new RegExp(`(${higlight})`, 'gi'));

    const result = (
        <React.Fragment>
            {
                parts.map((part, index) => {
                    if (returnAsString) {
                        return <span key={index} className={part.toLowerCase() === higlight.toLowerCase() ? 'text-highlight' : null} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(part) }} />
                    }

                    return <span key={index} className={part.toLowerCase() === higlight.toLowerCase() ? 'text-highlight' : null}>{part}</span>
                })
            }
        </React.Fragment>
    );

    if (returnAsString) {
        return ReactDOMServer.renderToStaticMarkup(result);
    }

    return result
}

const defaultWindowProps = {
    toolbar: 'no',
    scrollbar: 'yes',
    resizable: 'yes',
    width: 800,
    height: 800
}

export const openWindow = (url, windowProps = {}, frameName) => {
    windowProps = Object.assign({}, defaultWindowProps, windowProps);
    windowProps.left = window.screenX + window.outerWidth / 2 - windowProps.width / 2;
    windowProps.top = window.screenY + window.outerHeight / 2 - windowProps.height / 2;

    return window.open(url, frameName, qp.toString(windowProps).replace(/&/g, ','));
}

export const syncGetRequest = ({ url, paramsString = '', callback, error }) => {
    const request = new XMLHttpRequest();
    request.open('GET', `${url}?${paramsString}`, false);
    request.setRequestHeader('X-Session-Token', store.getState().auth.masterToken);
    request.send();

    if (request.status === 200) {
        callback(request.responseText);
    } else {
        error && error(request.statusText);
    }
}

export const getUrlParams = (url = window.location.search) => {
    return qp.toObject((url || '').replace('?', ''));
}

export const getUrlHashParams = (url = window.location.hash) => {
    return qp.toObject((url || '').replace('#', ''));
}

export const getTokenParam = (token, key = '') => {
    if (!token) {
        return null;
    }

    let result;

    try {
        const tokenParams = jwtDecode(token);
        const keys = key.split('.').filter(k => !!k);
        result = tokenParams;

        while (keys.length) {
            if (result) {
                result = result[keys.shift()];
            } else {
                return null;
            }
        }

        if (result && typeof result === 'string') {
            try {
                return decodeURI(result)
            } catch (e) {
                return result;
            }
        }
    } catch (e) {
        return null;
    }

    return result;
}
